import { createContext, use, useCallback, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { abrirSesion, alPerderSesion, ErrorApi, pedir } from "../api/cliente.ts";
import type { CuentaDisponible, Rol, Sesion } from "../api/tipos.ts";
import { puede, type Permiso } from "./permisos.ts";

/**
 * Estado de sesión de toda la app.
 *
 * No guarda ni ve un token: la sesión vive en cookies `httpOnly` que este código
 * no puede leer. Saber *si* hay sesión se resuelve preguntándoselo al backend
 * —un refresco al arrancar—, no mirando el almacenamiento del navegador.
 */

type Fase = "comprobando" | "fuera" | "dentro";

interface Valor {
  fase: Fase;
  sesion: Sesion | null;
  rol: Rol | undefined;
  cuenta: string | null;
  /** Nombre legible de la cuenta; el slug es una llave, no una etiqueta. */
  cuentaNombre: string | null;
  /** Quién está dentro. La cuenta no identifica a una persona. */
  usuario: { nombre: string; correo: string } | null;
  /** Atajo de cortesía para la interfaz; quien autoriza es el backend. */
  puedo: (permiso: Permiso) => boolean;
  entrar: (
    correo: string,
    contrasena: string,
    slug?: string,
  ) => Promise<{ elige?: CuentaDisponible[] }>;
  salir: () => Promise<void>;
}

const Contexto = createContext<Valor | null>(null);

/**
 * El slug de la cuenta activa es una etiqueta para la barra superior, no una
 * credencial: `sessionStorage` está bien para eso. El acceso sigue viviendo
 * exclusivamente en la cookie.
 */
const LLAVE_CUENTA = "4drando:cuenta";
const recordarCuenta = (slug: string | null) => {
  try {
    if (slug) sessionStorage.setItem(LLAVE_CUENTA, slug);
    else sessionStorage.removeItem(LLAVE_CUENTA);
  } catch {
    /* modo privado, o sin almacenamiento. No es crítico. */
  }
};
const cuentaRecordada = () => {
  try {
    return sessionStorage.getItem(LLAVE_CUENTA);
  } catch {
    return null;
  }
};

/**
 * El refresco del arranque, uno solo aunque el efecto corra dos veces.
 *
 * El refresco ROTA el token: el servidor revoca el viejo al emitir el nuevo.
 * Dos refrescos a la vez con la misma cookie —el efecto montado dos veces por
 * StrictMode en desarrollo, o un remontaje— hacen que el segundo llegue con un
 * token ya revocado, reciba 401, y la página caiga al login con la sesión
 * recién abierta (lo vio SPEC-029 al probar en local, 1-oct-2026). Compartir la
 * promesa lo evita. Se suelta al terminar: el próximo arranque vuelve a
 * preguntar.
 */
let arranque: Promise<Sesion> | null = null;
function refrescarAlArrancar(): Promise<Sesion> {
  arranque ??= pedir<Sesion>("/api/v1/sesion/refrescar", { metodo: "POST", cuerpo: {}, publica: true }).finally(() =>
    setTimeout(() => (arranque = null), 0),
  );
  return arranque;
}

export function ProveedorSesion({ children }: { children: ReactNode }) {
  const [fase, setFase] = useState<Fase>("comprobando");
  const [sesion, setSesion] = useState<Sesion | null>(null);
  const cache = useQueryClient();

  // Al arrancar, la única forma honesta de saber si hay sesión es preguntarlo.
  // Un refresco cuesta una petición y devuelve el rol vigente, que además puede
  // haber cambiado desde el último login.
  useEffect(() => {
    let vivo = true;
    refrescarAlArrancar()
      .then((s) => {
        if (!vivo) return;
        // El refresco ya trae la cuenta; lo recordado es solo el respaldo para
        // una versión del backend que todavía no la mande.
        setSesion({ ...s, cuenta: s.cuenta ?? cuentaRecordada() ?? undefined });
        setFase("dentro");
      })
      .catch(() => {
        if (!vivo) return;
        setFase("fuera");
      });
    return () => {
      vivo = false;
    };
  }, []);

  // Si el refresco automático del cliente se rinde, la app entera vuelve al
  // login sin que ninguna pantalla tenga que enterarse por su cuenta.
  useEffect(
    () =>
      alPerderSesion(() => {
        setSesion(null);
        setFase("fuera");
        recordarCuenta(null);
        cache.clear();
      }),
    [cache],
  );

  const entrar = useCallback<Valor["entrar"]>(
    async (correo, contrasena, slug) => {
      const r = await abrirSesion({ correo, contrasena, ...(slug ? { slug } : {}) });
      if (r.tipo === "elige") return { elige: r.datos.cuentas };

      const datos = r.datos as unknown as Sesion;
      recordarCuenta(datos.cuenta ?? slug ?? null);
      setSesion(datos);
      setFase("dentro");
      return {};
    },
    [],
  );

  const salir = useCallback(async () => {
    try {
      await pedir("/api/v1/sesion", { metodo: "DELETE", cuerpo: {}, publica: true });
    } catch (e) {
      // Cerrar sesión no puede fallar hacia el usuario: en el peor caso la
      // cookie caduca sola. Lo que no puede pasar es que se quede dentro.
      if (!(e instanceof ErrorApi)) throw e;
    }
    recordarCuenta(null);
    setSesion(null);
    setFase("fuera");
    cache.clear();
  }, [cache]);

  const valor = useMemo<Valor>(
    () => ({
      fase,
      sesion,
      rol: sesion?.rol,
      cuenta: sesion?.cuenta ?? null,
      cuentaNombre: sesion?.cuentaNombre ?? null,
      usuario: sesion?.usuario ?? null,
      puedo: (permiso) => puede(sesion?.rol, permiso),
      entrar,
      salir,
    }),
    [fase, sesion, entrar, salir],
  );

  return <Contexto value={valor}>{children}</Contexto>;
}

export function usarSesion(): Valor {
  const valor = use(Contexto);
  if (!valor) throw new Error("usarSesion() fuera de <ProveedorSesion>.");
  return valor;
}
